// src/pages/outsourcing_management/requisitions/RequisitionQuotePartPicker.tsx
//
// Quote + Part picker, the quote-side counterpart of
// RequisitionSalesOrderPartPicker. A requisition is raised from an order or
// from a quote, and until this existed the Quote "Outsource" entry point
// opened a form that could never be saved: the backend accepts quoteId +
// quoteItemId, but nothing in the UI could set them.
//
// Deliberately a sibling rather than a generalisation of the order picker.
// The two differ in more than their endpoint - a quote is identified by a
// name rather than an order number, its list response already carries full
// item objects (SimpleCartItemType) where the order list carries only ids,
// and its customer is a flat authorName rather than a billing address. Folding
// both into one component meant a prop for each of those differences and a
// body that reads as two components stitched together.
//
// Reuses the existing calls (apiGetCarts to search, apiGetCart to hydrate the
// parts and their model previews) rather than adding endpoints.
import { FunctionComponent, useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Grid,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import { apiGetCart, apiGetCarts } from "util/network/Carts";
import {
  ManufacturedProductDetailsType,
  ProductTypeEnum,
} from "types/products/ProductCommandsType";
import RequisitionPartPreview from "./RequisitionPartPreview";
import PartsSelectAllPaper, {
  PartsSelectAllContext,
} from "./PartsSelectAllPaper";
import { finishLabel, isPartEligible } from "./partEligibility";
import type { PickedPart } from "./RequisitionSalesOrderPartPicker";

// id is the quote item id (what the requisition stores); productId and name
// are display only, matching how parts render everywhere else in admin.
// modelId keys the preview image and is absent for non-manufactured parts.
type PickerQuoteItem = {
  id: number;
  productId: number;
  name: string;
  modelId?: number;
  /** Undefined when the part records no finish - see finishLabel. */
  finish?: string;
  /** Decides whether this part belongs under the chosen requisition type. */
  productType?: ProductTypeEnum;
  /** How many were quoted - the item line starts from this. */
  quantity?: number;
};

type PickerQuoteOption = {
  id: number;
  // Quotes carry a server default of "Untitled", but an older row may not.
  name?: string;
  customer?: string;
};

type PickerQuote = PickerQuoteOption & {
  items: PickerQuoteItem[];
  hydrated: boolean;
};

const DETAIL_SLOT_HEIGHT = 64;

// The customer slot keeps the same height whether it holds a skeleton or the
// customer, so the form does not jump when a quote is picked. Mirrors the
// order picker.
const detailSlotSx = {
  display: "flex",
  alignItems: "center",
  minHeight: DETAIL_SLOT_HEIGHT + 8,
  mt: 1,
};
const detailTextSx = { minWidth: 0, flexGrow: 1 };

const quoteLabel = (quote: PickerQuoteOption) =>
  quote.name ? `${quote.name} (#${quote.id})` : `Quote #${quote.id}`;

type PropsType = {
  token: string;
  quoteId?: number;
  /** The picked parts, in the order they were picked - one line each. */
  quoteItemIds: number[];
  onChange: (next: { quoteId?: number; quoteItemIds: number[] }) => void;
  quoteError?: string;
  quoteItemError?: string;
  disabled?: boolean;
  /** Finishing requisitions only: mark and refuse parts with no finish. */
  requireFinish?: boolean;
  /** The requisition's type code - the part list is narrowed to it. */
  typeCode?: string;
  /**
   * The picked parts once the quote is hydrated, for building their lines.
   * Fires on hydration too, so a requisition opened from an Outsource button
   * - where the part is already chosen and this picker is never touched -
   * gets its line as well. Empty until the quote has loaded: the parent
   * must not read an empty list as "nothing picked".
   */
  onPartsResolved?: (parts: PickedPart[]) => void;
};

const RequisitionQuotePartPicker: FunctionComponent<PropsType> = ({
  token,
  quoteId,
  quoteItemIds,
  onChange,
  quoteError,
  quoteItemError,
  disabled,
  requireFinish,
  typeCode,
  onPartsResolved,
}) => {
  const [quoteOptions, setQuoteOptions] = useState<PickerQuoteOption[]>([]);
  const [selectedQuote, setSelectedQuote] = useState<PickerQuote | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [hydrating, setHydrating] = useState(false);

  // Hydrate a quote handed in as a prefill (the Outsource button knows the
  // quote but not its parts), and re-hydrate whenever the id changes.
  useEffect(() => {
    if (!quoteId) {
      setSelectedQuote(null);
      return;
    }
    if (selectedQuote?.id === quoteId && selectedQuote.hydrated) return;

    setHydrating(true);
    apiGetCart(token, quoteId)
      .then((quote) => {
        setSelectedQuote({
          id: quote.id,
          name: quote.name,
          customer:
            [quote.author?.firstName, quote.author?.lastName]
              .filter(Boolean)
              .join(" ") ||
            quote.author?.email ||
            undefined,
          items: (quote.items ?? []).map((item) => ({
            id: item.id,
            productId: item.product?.id,
            name: item.name,
            modelId: (item.product?.details as ManufacturedProductDetailsType)
              ?.modelId,
            finish: finishLabel(
              item.product?.details as ManufacturedProductDetailsType
            ),
            productType: item.product?.productType,
            quantity: item.quantity,
          })),
          hydrated: true,
        });
      })
      .catch((err) => {
        // Was swallowed silently, which turned any failure in here - including
        // a throw while mapping the items - into an empty part list with no
        // hint as to why. The selection still round-trips via onChange.
        console.error("Failed to load parts for the selected %s", "quote", err);
      })
      .finally(() => setHydrating(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId, token]);

  // Debounced quote search-as-you-type.
  useEffect(() => {
    const term = inputValue.trim();
    if (!term) {
      setQuoteOptions([]);
      return;
    }
    setLoadingQuotes(true);
    const handle = setTimeout(() => {
      apiGetCarts(token, 1, 20, "id", "desc", undefined, term)
        .then((res) => {
          setQuoteOptions(
            (res.items ?? []).map((quote) => ({
              id: quote.id,
              name: quote.name,
              customer: quote.authorName,
            }))
          );
        })
        .catch(() => setQuoteOptions([]))
        .finally(() => setLoadingQuotes(false));
    }, 300);
    return () => clearTimeout(handle);
  }, [inputValue, token]);

  // A requisition asks for one kind of work, so only the parts that kind of
  // work applies to are offered - a sheet part on the quote has no business
  // appearing under a CNC requisition. Only manufactured parts (CNC, sheet, 3D
  // print) are ever offered - see partEligibility - and a finishing
  // requisition takes any of them with a finish on it.
  const parts = (selectedQuote?.items ?? []).filter((item) =>
    isPartEligible(item.productType, typeCode)
  );

  // In pick order, so the lines the parent builds from them keep that order.
  // An id the hydrated quote does not know is dropped silently: it was the
  // previous quote's, and the parent clears the ids on a quote change.
  const selectedParts = quoteItemIds
    .map((id) => selectedQuote?.items.find((item) => item.id === id))
    .filter((item): item is PickerQuoteItem => !!item);

  // What "Select all" picks: every offered part a click could pick by hand,
  // so a finishing requisition skips the parts it refuses anyway.
  const selectable = parts.filter((item) => !requireFinish || !!item.finish);
  const allSelected =
    selectable.length > 0 &&
    selectable.every((item) => quoteItemIds.includes(item.id));
  // The "Select all" row at the top of the dropdown - see PartsSelectAllPaper.
  const selectAll = {
    total: selectable.length,
    selected: selectable.filter((item) => quoteItemIds.includes(item.id)).length,
    onToggle: () =>
      onChange({
        quoteId: selectedQuote?.id,
        quoteItemIds: allSelected ? [] : selectable.map((item) => item.id),
      }),
  };

  // A type change can leave picks behind that the new type would never have
  // offered - a sheet part under a requisition switched to CNC, a part with
  // no finish once it became a finishing requisition. Drop them, the same
  // way the list refuses them fresh; the rest of the picks stay.
  const ineligibleIds = selectedParts
    .filter((item) => !selectable.some((s) => s.id === item.id))
    .map((item) => item.id);
  const ineligibleKey = ineligibleIds.join(",");
  useEffect(() => {
    // Not while loading: the picks may belong to a record not resolved yet.
    if (hydrating || !selectedQuote || ineligibleIds.length === 0) return;
    onChange({
      quoteId: selectedQuote.id,
      quoteItemIds: quoteItemIds.filter((id) => !ineligibleIds.includes(id)),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ineligibleKey, hydrating]);

  // Keyed on a string of the resolved parts, not the array: the array is a
  // fresh object every render and would re-fire this on each one.
  const resolvedKey = selectedParts
    .map((item) => `${item.id}:${item.quantity ?? ""}`)
    .join(",");
  useEffect(() => {
    onPartsResolved?.(
      selectedParts.map((item) => ({
        quoteItemId: item.id,
        productId: item.productId,
        name: item.name,
        quantity: item.quantity,
      }))
    );
    // The callback is deliberately out of the deps: parents pass an inline
    // function, and including it would re-fire on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedKey]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <Autocomplete
          size="small"
          disabled={disabled}
          options={quoteOptions}
          loading={loadingQuotes || hydrating}
          value={selectedQuote}
          filterOptions={(options) => options} // server-side search already applied
          getOptionLabel={quoteLabel}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onInputChange={(_, value) => setInputValue(value)}
          renderOption={(props, quote) => (
            <li {...props} key={quote.id}>
              <Box sx={detailTextSx}>
                <Typography variant="body2" noWrap>
                  {quoteLabel(quote)}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  noWrap
                >
                  {quote.customer || "No customer on this quote"}
                </Typography>
              </Box>
            </li>
          )}
          onChange={(_, quote) => {
            // Parts stay empty until the effect above hydrates them; the label
            // renders off the name in the meantime.
            setSelectedQuote(
              quote ? { ...quote, items: [], hydrated: false } : null
            );
            onChange({ quoteId: quote?.id, quoteItemIds: [] });
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Quote"
              required
              error={!!quoteError}
              helperText={quoteError ?? "Search by quote name or customer"}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingQuotes || hydrating ? (
                      <CircularProgress color="inherit" size={16} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        {selectedQuote && (
          <Box sx={detailSlotSx}>
            {selectedQuote.hydrated || selectedQuote.customer ? (
              <Box sx={detailTextSx}>
                <Typography variant="body2" noWrap>
                  {selectedQuote.customer || "-"}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  noWrap
                >
                  {selectedQuote.customer
                    ? `Quote #${selectedQuote.id}`
                    : "No customer on this quote"}
                </Typography>
              </Box>
            ) : (
              <Box sx={detailTextSx}>
                <Skeleton variant="text" width="70%" />
                <Skeleton variant="text" width="50%" />
              </Box>
            )}
          </Box>
        )}
      </Grid>
      <Grid item xs={12} sm={6}>
        <PartsSelectAllContext.Provider value={selectAll}>
          <Autocomplete
            multiple
            disableCloseOnSelect
            PaperComponent={PartsSelectAllPaper}
            size="small"
            disabled={disabled || !selectedQuote || hydrating}
            loading={hydrating}
            options={parts}
            value={selectedParts}
            getOptionLabel={(item) =>
              item.name ? `#${item.productId} ${item.name}` : `#${item.productId}`
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, items) =>
              onChange({
                quoteId: selectedQuote?.id,
                quoteItemIds: items.map((item) => item.id),
              })
            }
            getOptionDisabled={(item) => !!requireFinish && !item.finish}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((item, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={item.id}
                  size="small"
                  label={
                    item.name ? `#${item.productId} ${item.name}` : `#${item.productId}`
                  }
                />
              ))
            }
            renderOption={(props, item, { selected }) => {
              const blocked = !!requireFinish && !item.finish;
              return (
                <li {...props} key={item.id}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Checkbox
                      size="small"
                      checked={selected}
                      tabIndex={-1}
                      sx={{ p: 0 }}
                    />
                    <RequisitionPartPreview
                      token={token}
                      modelId={item.modelId}
                      size={40}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <span>
                        {item.name
                          ? `#${item.productId} ${item.name}`
                          : `#${item.productId}`}
                      </span>
                      {/* Only shown for a finishing requisition: everywhere
                          else the finish is not what is being ordered, and
                          this is noise. */}
                      {requireFinish && (
                        <Typography
                          variant="caption"
                          display="block"
                          color={blocked ? "error" : "text.secondary"}
                        >
                          {item.finish ?? "No finish recorded - cannot be finished"}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Parts"
                required={selectedParts.length === 0}
                error={!!quoteItemError}
                helperText={
                  quoteItemError ??
                  (hydrating
                    ? "Loading parts..."
                    : !selectedQuote
                    ? "Select a Quote first"
                    : parts.length
                    ? "Select one or more parts from this quote - one line each"
                    // Says why the list is empty. Without this a type with no
                    // matching parts looks identical to a failed load.
                    : `No parts on this quote match this requisition type`)
                }
              />
            )}
          />
        </PartsSelectAllContext.Provider>
      </Grid>
    </Grid>
  );
};

export default RequisitionQuotePartPicker;
